import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { UsuarioService } from '../../../../../services/usuario.service';
import { allRoles, liquidationTypes } from '../../../../../data/master_lists';
import { isSalesRepRole } from '../../../../../utilities/role-utilities';

@Component({
    selector: 'app-create-user-modal',
    templateUrl: './create-user-modal.component.html',
    styleUrls: ['./create-user-modal.component.scss'],
    standalone: false
})
export class CreateUserModalComponent implements OnInit, OnDestroy {
    formCreateUser!: UntypedFormGroup;
    allRolesOptions = allRoles;
    liquidationTypes = liquidationTypes;
    loading = false;
    private roleSub?: Subscription;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private usuarioService: UsuarioService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        this.formCreateUser = this.fb.group({
            name: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            role: ['', Validators.required],
            liquidation_type: [''],  // sin validador estático — se aplica dinámicamente
            password: ['', [Validators.required, Validators.minLength(6)]],
            password2: ['', [Validators.required, Validators.minLength(6)]]
        });

        // Escucha cambios de rol y actualiza la validación de liquidation_type dinámicamente.
        this.roleSub = this.formCreateUser.get('role')!.valueChanges.subscribe(role => {
            this.updateLiquidationTypeValidation(role);
        });
    }

    private updateLiquidationTypeValidation(role: string): void {
        const liqControl = this.formCreateUser.get('liquidation_type');
        if (!liqControl) return;
        if (isSalesRepRole(role)) {
            liqControl.setValidators([Validators.required]);
        } else {
            liqControl.clearValidators();
            liqControl.setValue('');
        }
        liqControl.updateValueAndValidity({ emitEvent: false });
    }

    get isSalesRep(): boolean {
        return isSalesRepRole(this.formCreateUser?.get('role')?.value);
    }

    async createUser(): Promise<void> {
        if (!this.formCreateUser.valid) {
            this.toastr.warning('All fields are required');
            this.formCreateUser.markAllAsTouched();
            return;
        }

        const { password, password2, ...userData } = this.formCreateUser.value;

        if (password !== password2) {
            this.toastr.warning('Passwords do not match');
            return;
        }

        this.loading = true;
        try {
            const response = await this.usuarioService.createUser({ ...userData, password });
            this.toastr.success(response?.msg || 'User created successfully');
            this.activeModal.close(true);
        } catch (error: any) {
            this.toastr.error(error.error?.msg || 'Error creating user');
        } finally {
            this.loading = false;
        }
    }

    closeModal(): void {
        this.activeModal.dismiss();
    }

    ngOnDestroy(): void {
        this.roleSub?.unsubscribe();
    }
}
